'use client';

import React, { useRef, useState } from 'react';
import {
  IRegionFAQ,
  IRegionFeaturedResort, IRegionShort,
} from '@/api/region/types';
import { Main } from '@/components/resort/Layout/Main';
import { Side } from '@/components/resort/Layout/Side';
import { SidebarAd } from '@/tracking/ads/components/SidebarAd';
import { RouteAndAnalyticsProps } from '@/page-components/pathTypes';
import { IRegionLayout } from '@/page-components/region/_layout';
import { Routes } from '@/routes';
import { useMustacheTranslation, useOTSTranslation } from '@/i18n/useOTSTranslation';
import { I18nKey } from '@/i18n/types';
import { IResortTopRated, ResortTopRated, IFeaturedResort } from '@/api/home/types';
import {
  IRelatedNews, IResortPageSnowInfo, IResortPathInfo, IDescriptionsResort,
} from '@/api/resort/types';
import { FeaturedResort } from '@/components/home/FeaturedResort';
import PlanYourTrip from '@/components/common/PlanYourTrip/RegionWidget';
import { isMainLocale } from '@/util/i18nFlags';
import { spansMultipleRegions } from '@/util/spansMultipleRegions';
import { Hero400Error } from '@/components/400';
import RelatedArticles from '@/components/resort/RelatedArticles';
import { SkiGptPromotion } from '@/components/common/SkiGptPromotion';
import { SeoSection } from '@/components/common/SeoSection';
import { DataTable } from '@/components/common/DataTable';
import { CategoryChips } from '@/components/common/CategoryChips';
import { ResortRankCard } from '@/components/common/ResortRankCard';
import { ItemsContainer } from '@/components/common/ItemsContainer';
import { ResortNameLink } from '@/components/common/ResortNameLink';
import { PageBody } from '@/components/common/PageBody';
import { RegionFAQ } from '@/components/region/RegionFAQ';
import { Heading } from '@/components/ui/heading';

// Top-rated categories surfaced in the v3 sandbox, in order. Labels reuse the
// existing, already-translated region.resortGuide.*Title keys.
const TOP_CATEGORIES: { key: ResortTopRated; labelKey: I18nKey['common'] }[] = [
  { key: ResortTopRated.overall, labelKey: 'region.resortGuide.overallTitle' },
  { key: ResortTopRated.allMtnTerrain, labelKey: 'region.resortGuide.allMtnTerrainTitle' },
  { key: ResortTopRated.expert, labelKey: 'region.resortGuide.expertTitle' },
  { key: ResortTopRated.terrainPark, labelKey: 'region.resortGuide.terrainParkTitle' },
  { key: ResortTopRated.beginner, labelKey: 'region.resortGuide.beginnerTitle' },
  { key: ResortTopRated.familyFriendly, labelKey: 'region.resortGuide.familyFriendlyTitle' },
  { key: ResortTopRated.apresSki, labelKey: 'region.resortGuide.apresSkiTitle' },
];

export type RegionGuidePageProps = {
  featured: IRegionFeaturedResort,
  featuredResortByIndex: IResortPageSnowInfo | null,
  topRatedByCategory: Partial<Record<ResortTopRated, Array<IResortTopRated>>>,
  regionResortListGuide: Array<IResortPathInfo>,
  regionFAQ: IRegionFAQ,
  pathInfo: IRegionShort,
  numResorts: number,
  relatedNews: IRelatedNews | null,
  featuredResortDescriptionTabs: Array<IDescriptionsResort> | null,
} & RouteAndAnalyticsProps & IRegionLayout;

export const DEBUG_TAG = 'region-ski-resorts-page';
export const TEMPLATE_ID = '3.1.1';
export const TEMPLATE_NAME = 'Region Guide Page';

const Top10Section = ({
  regionName, topRatedByCategory,
}: {
  regionName: string;
  topRatedByCategory: RegionGuidePageProps['topRatedByCategory'];
}) => {
  const { t } = useOTSTranslation('common');
  const available = TOP_CATEGORIES
    .filter((c) => (topRatedByCategory[c.key] ?? []).length > 0)
    .map((c) => ({ key: c.key, label: t(c.labelKey) }));
  const [active, setActive] = useState<ResortTopRated>(
    available[0]?.key ?? ResortTopRated.overall,
  );
  const items = (topRatedByCategory[active] ?? []).slice(0, 12);

  if (available.length === 0 || items.length === 0) return null;

  return (
    <section>
      <Heading level={2} className="mb-4">
        {t('region.skiResorts.topInRegion', { region: regionName })}
      </Heading>

      <CategoryChips
        items={available}
        active={active}
        onSelect={setActive}
        className="mb-5"
        ariaLabel={t('common.topRatedCategories')}
      />

      <ItemsContainer
        key={active}
        desktopLayout="grid"
        desktopColumns={3}
        desktopPageSize={6}
        ariaLabel={t('region.skiResorts.topResortsAria', { category: available.find((c) => c.key === active)?.label })}
      >
        {items.map((item, i) => (
          <ItemsContainer.Item key={item.resort.uuid}>
            <ResortRankCard
              rank={i + 1}
              title={item.resort.title}
              image={item.resort.image || item.resort.smallImage}
              href={Routes.resortTripPlanningOverview(item.resortPath)}
            />
          </ItemsContainer.Item>
        ))}
      </ItemsContainer>
    </section>
  );
};

const AllResortsTable = ({
  regionName, resorts,
}: { regionName: string; resorts: Array<IResortPathInfo> }) => {
  const { t } = useOTSTranslation('common');
  if (!resorts || resorts.length === 0) return null;
  // Pass regions (Ikon, Epic, …) gather resorts from several primary
  // regions; name alone doesn't say where a row is.
  const showRegion = spansMultipleRegions(resorts);
  return (
    <section>
      <Heading level={2} className="mb-4">
        {t('region.skiResorts.allInRegion', { region: regionName })}
      </Heading>
      {/* Single column for now. When the region API exposes per-resort
          stats (skiable / trails / lifts / base / vertical / avg snow)
          we can fan this out — see docs/api-wishlist.md. */}
      <DataTable>
        <DataTable.Head>
          <DataTable.HeadRow>
            <DataTable.HeadCell>{t('searchResults.resort')}</DataTable.HeadCell>
          </DataTable.HeadRow>
        </DataTable.Head>
        <DataTable.Body>
          {resorts.map((r, i) => (
            <DataTable.Row key={r.uuid} zebra={i % 2 === 0}>
              <DataTable.RowHeader>
                <ResortNameLink
                  resort={r}
                  href={Routes.resortTripPlanningOverview(r)}
                  showRegion={showRegion}
                />
              </DataTable.RowHeader>
            </DataTable.Row>
          ))}
        </DataTable.Body>
      </DataTable>
    </section>
  );
};

export default function RegionGuidePage({
  featuredResortByIndex,
  topRatedByCategory,
  pathInfo,
  regionResortListGuide,
  regionFAQ,
  numResorts,
  relatedNews,
  featuredResortDescriptionTabs,
}: RegionGuidePageProps) {
  const { t } = useOTSTranslation('common');
  const m = useMustacheTranslation('common');
  const refScroll = useRef<HTMLDivElement>(null);

  const showLodgingWidget = isMainLocale();

  if (!regionFAQ || !regionResortListGuide || !topRatedByCategory) {
    return <Hero400Error light />;
  }

  const seoTitle = pathInfo.isLogical ? t('region.resortGuide.logicSeoTitle', { region: pathInfo.title })
    : t('region.resortGuide.seoTitle', { region: pathInfo.title });
  const seoText = pathInfo.isLogical ? m('region.resortGuide.logicSeoText', {
    region: pathInfo.title,
    numresorts: numResorts,
  }) : m('region.resortGuide.seoText', {
    region: pathInfo.title,
    numresorts: numResorts,
  });

  const featuredResort: IFeaturedResort | null = featuredResortByIndex ? {
    uuid: featuredResortByIndex.uuid,
    title: featuredResortByIndex.title,
    region: featuredResortByIndex.region,
    image: featuredResortByIndex.image,
    smallImage: featuredResortByIndex.smallImage ?? '',
    video: featuredResortByIndex.video ?? null,
    website: featuredResortByIndex.website ?? null,
    phone: featuredResortByIndex.phone ?? null,
    isPartner: featuredResortByIndex.isPartner,
    twitter: featuredResortByIndex.twitter ?? null,
    liftsUrl: featuredResortByIndex.liftsUrl ?? null,
    rentalUrl: featuredResortByIndex.rentalUrl ?? null,
    lessonsUrl: featuredResortByIndex.lessonsUrl ?? null,
    socialMediaAccounts: featuredResortByIndex.socialMediaAccounts ?? [],
  } : null;

  return (
    <>
      <Main>
        <PageBody>
          <SeoSection title={seoTitle}>{seoText}</SeoSection>

          {featuredResort && (
            <FeaturedResort
              resort={featuredResort}
              regionResorts={regionResortListGuide}
              resortDescriptionTabs={featuredResortDescriptionTabs ?? undefined}
              isOnRegionSkiResorts
              utmCampaign="region_widget"
            />
          )}

          <Top10Section
            regionName={pathInfo.title}
            topRatedByCategory={topRatedByCategory}
          />

          <AllResortsTable
            regionName={pathInfo.title}
            resorts={regionResortListGuide}
          />

          {regionFAQ && (
            <RegionFAQ
              regionTitle={pathInfo.title}
              regionFAQ={regionFAQ}
              refScroll={refScroll}
              placeholders={{
                region: pathInfo.title,
                NUMRESORTS: String(numResorts),
                numresorts: String(numResorts),
              }}
            />
          )}

          <SkiGptPromotion
            templateId={TEMPLATE_ID}
            name={pathInfo.title}
          />

          {showLodgingWidget && (
            <PlanYourTrip
              region={pathInfo}
              hasSkiRental={false}
              hasCarRental={false}
              hasFlights={false}
              isPartner={false}
            />
          )}
        </PageBody>
      </Main>
      <Side>
        <RelatedArticles
          relatedNewsList={relatedNews}
        />
        <SidebarAd isRegion id={pathInfo.uuid} />
      </Side>
      {/* JSON-LD renders from the RSC page (see app/[region]/ski-resorts/page.tsx). */}
    </>
  );
}

// fetchProps moved to ./<filename>.server.ts so the App Router RSC page can
// import the data fetcher without pulling in this 'use client' bundle.
